// src/context/UserContext.tsx
import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalUserInfo,
  setLocalEmployeeId,
  removeLocalEmployeeId,
  setLocalUserRoles,
  setLocalZulipAuth,
} from "@/lib/storeManager";
import {
  AuthService,
  UserPreferencesService,
  DynamicModuleService,
  UserRoleService,
  UploadService,
  fetchPersistAndCacheCurrentUserDetails,
  getCurrentUserDisplayName,
  normalizeCurrentUserEmployee,
} from "@/services/APIs";
import { useQueryClient } from "@tanstack/react-query";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: any;
}

// Proactively refresh the access token on this fixed cadence (14 minutes),
// keeping it ahead of the typical 15-minute access-token expiry.
const TOKEN_REFRESH_INTERVAL_MS = 14 * 60 * 1000;

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  preferencesData: any; // Added preferences data
  fetchPreferencesData: () => Promise<void>; // Function to fetch preferences data
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<any>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: () => Promise<any>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<any>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<any>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  // Local state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] = useState<any>(null); // Preferences data state

  // Bootstrap from localStorage
  useEffect(() => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (access && refresh) {
      try {
        const decoded = jwtDecode<DecodedToken>(access);
        setJwtToken(access);
        setRefreshToken(refresh);
        setDecodedToken(decoded);
      } catch {
        const preservedTenant = getTenantId();
        clearLocalStorage();
        if (preservedTenant !== null) {
          setLocalTenantId(preservedTenant);
        }
      }
    }
  }, []);

  const isAuthenticated =
    Boolean(jwtToken && decodedToken) && decodedToken!.exp * 1000 > Date.now();

  // Fetch preferences data function
  const fetchPreferencesData = useCallback(async () => {
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferencesData();
    }
  }, [isAuthenticated, decodedToken, refreshToken, fetchPreferencesData]);

  // Employee ID in localStorage is only a cache; JWT user id is authoritative — re-sync from API so DevTools edits cannot impersonate another employee.
  const syncEmployeeIdFromApi = useCallback(async () => {
    const access = jwtToken ?? getAccessToken();
    if (!access) {
      return;
    }
    let decoded: DecodedToken;
    try {
      decoded = jwtDecode<DecodedToken>(access);
    } catch {
      return;
    }
    if (decoded.exp * 1000 <= Date.now()) {
      return;
    }
    const userId = decoded.userId ?? decoded.sub ?? getUserInfo()?.id;
    if (!userId) {
      return;
    }
    try {
      // Uses GET /pts_app/current-user-details/ (see DynamicModuleService.getEmployeeByUserId)
      // Old: /formily/dynamic-model/PTS%20Emp/?users__eq={userId}
      const employeeData = await DynamicModuleService.getEmployeeByUserId(
        String(userId)
      );
      if (employeeData?.id) {
        setLocalEmployeeId(String(employeeData.id));
      } else {
        removeLocalEmployeeId();
      }
    } catch (employeeError) {
      console.warn("Failed to sync employee ID from API:", employeeError);
    }
  }, [jwtToken]);

  // Sync user roles from API on load and when user returns to tab (source of truth is server, not localStorage)
  const syncUserRolesFromApi = useCallback(async () => {
    if (!decodedToken) {
      return;
    }
    const userId = decodedToken.userId ?? decodedToken.sub ?? getUserInfo()?.id;
    if (!userId) {
      return;
    }
    try {
      const roleData = await UserRoleService.getUserCurrentRole(userId);
      if (Array.isArray(roleData?.roles)) {
        setLocalUserRoles(roleData.roles);
      }
    } catch (roleError) {
      console.warn("Failed to sync user roles from API:", roleError);
    }
  }, [decodedToken]);

  useEffect(() => {
    syncEmployeeIdFromApi();
  }, [jwtToken, syncEmployeeIdFromApi]);

  useEffect(() => {
    if (isAuthenticated && decodedToken) {
      syncUserRolesFromApi();
    }
  }, [isAuthenticated, decodedToken, syncUserRolesFromApi]);

  // Re-sync when user returns to the tab (e.g. admin changed role in another session)
  useEffect(() => {
    const access = jwtToken ?? getAccessToken();
    if (!access) {
      return;
    }
    const onFocus = () => {
      syncEmployeeIdFromApi();
      if (isAuthenticated) {
        syncUserRolesFromApi();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthenticated, jwtToken, syncEmployeeIdFromApi, syncUserRolesFromApi]);

  // Calls /auth/token-refresh/ with the stored refresh token, then persists the
  // new access token (and rotated refresh token, if returned) to both state and
  // localStorage so the axios interceptor sends the latest credentials.
  const performTokenRefresh = useCallback(async () => {
    const currentRefresh = getRefreshToken();
    if (!currentRefresh) {
      return;
    }
    try {
      const response = await AuthService.refreshToken();
      const newAccess = response?.access ?? response?.data?.access;
      const newRefresh = response?.refresh ?? response?.data?.refresh;

      if (newAccess) {
        setJwtToken(newAccess);
        setDecodedToken(jwtDecode<DecodedToken>(newAccess));
        setLocalAccessToken(newAccess);
      }
      if (newRefresh) {
        setRefreshToken(newRefresh);
        setLocalRefreshToken(newRefresh);
      }
    } catch {
      await handleLogout();
    }
    // handleLogout is a stable handler defined below; intentionally omitted from deps.
  }, []);

  // Fire the refresh on a fixed 14-minute cadence while the user is authenticated.
  useEffect(() => {
    if (!isAuthenticated || !refreshToken) {
      return;
    }
    const intervalId = window.setInterval(
      performTokenRefresh,
      TOKEN_REFRESH_INTERVAL_MS
    );
    return () => window.clearInterval(intervalId);
  }, [isAuthenticated, refreshToken, performTokenRefresh]);

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = { access: string; refresh: string };
    const payload = (response?.data ?? response) as TokenPayload;
    const access = payload?.access;
    const refresh = payload?.refresh;

    if (response) {
      setJwtToken(access);
      setRefreshToken(refresh);
      const decoded = jwtDecode<DecodedToken>(access);
      setDecodedToken(decoded);
      setLocalAccessToken(access);
      setRefreshToken(refresh);
      setLocalRefreshToken(refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;

      let employeeImageUrl = null;
      let currentUserDetails = null;
      try {
        currentUserDetails =
          await fetchPersistAndCacheCurrentUserDetails(queryClient);
        const employeeData = normalizeCurrentUserEmployee(currentUserDetails);
        if (employeeData?.img) {
          try {
            const imageData = await UploadService.getFileById({
              id: employeeData.img,
            });
            employeeImageUrl =
              imageData?.file_path_thumbnail ?? imageData?.file_path ?? null;
          } catch (imageError: any) {
            console.error("Failed to fetch employee image:", imageError);
          }
        }
      } catch (employeeError: any) {
        console.error("Failed to fetch current user details:", employeeError);
      }

      const apiDisplayName = getCurrentUserDisplayName(currentUserDetails);

      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
        full_name:
          apiDisplayName || `${first_name} ${last_name}`.trim() || undefined,
        ...(currentUserDetails?.initials && {
          initials: currentUserDetails.initials,
        }),
        ...(employeeImageUrl && { img: employeeImageUrl }),
      });

      // Fetch and store user roles after signup
      try {
        const roleData = await UserRoleService.getUserCurrentRole(
          response.user.id
        );
        if (roleData?.roles) {
          setLocalUserRoles(roleData.roles);
          console.log("User roles stored:", roleData.roles);
        }
      } catch (roleError: any) {
        console.error("Failed to fetch user roles:", roleError);
        // Don't block signup if role fetch fails
      }

      fetchPreferencesData();
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    const response = await AuthService.login({
      email: userEmail,
      password: userPassword,
    });
    if (response) {
      console.log(response);
      setJwtToken(response.access);
      setRefreshToken(response.refresh);

      const decoded = jwtDecode<DecodedToken>(response.access);
      setDecodedToken(decoded);
      setLocalAccessToken(response.access);
      setRefreshToken(response.refresh);
      setLocalRefreshToken(response.refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      let employeeImageUrl = null;
      let currentUserDetails = null;
      try {
        currentUserDetails =
          await fetchPersistAndCacheCurrentUserDetails(queryClient);
        const employeeData = normalizeCurrentUserEmployee(currentUserDetails);
        if (employeeData?.img) {
          try {
            const imageData = await UploadService.getFileById({
              id: employeeData.img,
            });
            employeeImageUrl =
              imageData?.file_path_thumbnail ?? imageData?.file_path ?? null;
          } catch (imageError: any) {
            console.error("Failed to fetch employee image:", imageError);
          }
        }
      } catch (employeeError: any) {
        console.error("Failed to fetch current user details:", employeeError);
      }

      const apiDisplayName = getCurrentUserDisplayName(currentUserDetails);

      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
        full_name:
          apiDisplayName || `${first_name} ${last_name}`.trim() || undefined,
        ...(currentUserDetails?.initials && {
          initials: currentUserDetails.initials,
        }),
        ...(employeeImageUrl && { img: employeeImageUrl }),
      });

      // Fetch and store user roles after login
      try {
        const roleData = await UserRoleService.getUserCurrentRole(
          response.user.id
        );
        if (roleData?.roles) {
          setLocalUserRoles(roleData.roles);
          console.log("User roles stored:", roleData.roles);
        }
      } catch (roleError: any) {
        console.error("Failed to fetch user roles:", roleError);
        // Don't block login if role fetch fails
      }

      // Fetch and store Zulip token after login
      try {
        const zulipAuth = await AuthService.getZulipToken();
        if (zulipAuth) {
          setLocalZulipAuth(zulipAuth);
        }
      } catch (zulipError: any) {
        console.warn("Failed to fetch Zulip auth token:", zulipError);
      }

      fetchPreferencesData();
      return response;
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleLogout = async () => {
    const response = await AuthService.logout();
    setJwtToken(null);
    setRefreshToken(null);
    setDecodedToken(null);
    const preservedTenant = getTenantId();
    clearLocalStorage();
    window.location.reload();
    if (preservedTenant !== null) {
      setLocalTenantId(preservedTenant);
    }
    return response;
  };

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Export the context for use in other files
export { UserContext };
